import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Image, Film, Newspaper, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Medias = () => {
  const [images, setImages] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [pressKit, setPressKit] = useState(false);

  useEffect(() => {
    Promise.all([axios.get(`${API}/gallery`), axios.get(`${API}/press-kit/status`)])
      .then(([gRes, pkRes]) => { setImages(gRes.data); setPressKit(pkRes.data.available); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const categories = [...new Set(images.map(i => i.category))];
  const filtered = filter === 'all' ? images : images.filter(i => i.category === filter);

  return (
    <div className="min-h-screen pt-16">
      {/* PAGE HERO */}
      <section className="relative py-24 lg:py-32 bg-[#1E3A5F] overflow-hidden" data-testid="medias-hero">
        <div className="absolute inset-0 opacity-10" aria-hidden="true" />
        <div className="relative container-institutional">
          <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-4">Médias</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">Médiathèque</h1>
          <p className="text-lg text-white/70 max-w-2xl leading-relaxed">Retrouvez les photos, vidéos et documents de presse du Forum URBANIK.</p>
        </div>
      </section>

      {/* PHOTO GALLERY */}
      <section className="py-20 lg:py-28" data-testid="photo-gallery">
        <div className="container-institutional">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-3"><Image className="w-4 h-4 inline mr-2" />Photos</p>
              <h2 className="text-3xl font-bold text-[#1E3A5F]">Galerie Photo</h2>
            </div>
            <div className="hidden sm:flex items-center gap-3">
              <button onClick={() => setFilter('all')} className={`px-4 py-2 text-sm rounded-full transition-colors ${filter === 'all' ? 'bg-[#1E3A5F] text-white' : 'bg-[#F7F9FC] text-[#4A5568] hover:bg-[#E1E8F0]'}`}>Tous</button>
              {categories.map(c => (
                <button key={c} onClick={() => setFilter(c)} className={`px-4 py-2 text-sm rounded-full transition-colors capitalize ${filter === c ? 'bg-[#1E3A5F] text-white' : 'bg-[#F7F9FC] text-[#4A5568] hover:bg-[#E1E8F0]'}`}>{c}</button>
              ))}
            </div>
          </div>
          {loading ? <p className="text-center text-[#94A3B8] py-12">Chargement...</p> : filtered.length === 0 ? (
            <p className="text-center text-[#94A3B8] py-12">Aucune photo disponible.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filtered.map((img, i) => (
                <div key={img.id || img.filename || i} className="relative rounded-lg overflow-hidden aspect-square bg-[#F7F9FC] group" data-testid={`gallery-image-${i}`}>
                  <img src={img.url || `${API}/uploads/images/${img.filename}`} alt={img.title || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  {img.title && <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3"><p className="text-white text-xs font-medium">{img.title}</p></div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* PRESS / DOWNLOADS */}
      <section className="py-20 lg:py-28 bg-[#F7F9FC]" data-testid="press-section">
        <div className="container-institutional">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-8 bg-white border border-[#E1E8F0] rounded-lg">
              <div className="flex items-center gap-3 mb-4"><Newspaper className="w-5 h-5 text-[#1E3A5F]" /><h3 className="text-lg font-bold text-[#1E3A5F]">Presse</h3></div>
              <p className="text-sm text-[#6B7C93] leading-relaxed mb-6">Retrouvez les communiqués de presse et articles liés au Forum URBANIK 2026.</p>
              {pressKit && (
                <a href={`${API}/press-kit/download`} target="_blank" rel="noopener noreferrer" className="btn-outline text-sm px-6 py-3" data-testid="press-download">
                  <Download className="w-4 h-4 mr-2" /> Dossier de Presse
                </a>
              )}
            </div>
            <div className="p-8 bg-white border border-[#E1E8F0] rounded-lg">
              <div className="flex items-center gap-3 mb-4"><Film className="w-5 h-5 text-[#1E3A5F]" /><h3 className="text-lg font-bold text-[#1E3A5F]">Vidéos</h3></div>
              <p className="text-sm text-[#6B7C93] leading-relaxed mb-6">Les vidéos des conférences et moments forts seront disponibles après l'événement.</p>
              <span className="text-sm text-[#94A3B8] italic">Bientôt disponible</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#1E3A5F]" data-testid="medias-cta">
        <div className="container-institutional text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Participez à URBANIK 2026</h2>
          <p className="text-white/70 max-w-xl mx-auto mb-8">Vivez l'expérience en direct et faites partie de l'événement.</p>
          <Link to="/participer" className="btn-primary text-base px-8 py-4">S'inscrire <ArrowRight className="w-5 h-5 ml-2" /></Link>
        </div>
      </section>
    </div>
  );
};

export default Medias;
